import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Building2, Car, Check, MapPin, Phone, User } from 'lucide-react';

const SocietyRegistration = () => {
  const { society_code } = useParams();
  const navigate = useNavigate();
  const [society, setSociety] = useState(null);
  const [buildings, setBuildings] = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');
  const [flats, setFlats] = useState([]);
  const [selectedFlat, setSelectedFlat] = useState(null);
  const [form, setForm] = useState({ phone: '', name: '', password: '' });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitResult, setSubmitResult] = useState(null);

  const fetchSociety = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/society/${society_code}/info`);
      setSociety(res.data.society);
      setBuildings(res.data.buildings);
    } catch {
      toast.error('Society not found');
    } finally {
      setLoading(false);
    }
  }, [society_code]);

  const fetchFlats = useCallback(async (buildingId) => {
    try {
      const res = await axios.get(`${API}/society/${society_code}/flats/${buildingId}`);
      setFlats(res.data.flats);
    } catch {
      toast.error('Failed to load flats');
    }
  }, [society_code]);

  useEffect(() => {
    fetchSociety();
  }, [fetchSociety]);

  useEffect(() => {
    if (selectedBuilding) fetchFlats(selectedBuilding);
    else setFlats([]);
    setSelectedFlat(null);
  }, [selectedBuilding, fetchFlats]);

  const handleSubmit = async () => {
    if (!selectedBuilding || !selectedFlat || !form.phone || !form.name || !form.password) {
      toast.error('Please fill all fields');
      return;
    }
    if (form.phone.length !== 10) {
      toast.error('Enter a valid 10-digit phone number');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/society-register`, {
        society_code,
        building_id: selectedBuilding,
        flat_id: selectedFlat.id,
        phone: form.phone,
        name: form.name,
        password: form.password
      });
      setSubmitResult(res.data);
      setSubmitted(true);
      toast.success('Registration submitted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
        <p style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>Loading...</p>
      </div>
    );
  }

  if (!society) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F4F4F5' }}>
        <div className="text-center p-8 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
          <p className="text-lg font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#DC2626' }}>Society Not Found</p>
          <p className="text-sm mt-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>This registration link is invalid or the society is inactive.</p>
        </div>
      </div>
    );
  }

  if (submitted && submitResult) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#F4F4F5' }}>
        <div className="w-full max-w-md p-8 border text-center" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7', borderWidth: '2px' }} data-testid="registration-success">
          <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: '#10B981' }}>
            <Check size={40} style={{ color: '#FFFFFF' }} />
          </div>
          <h1 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#09090B' }}>Registration Submitted</h1>
          <p className="text-sm mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
            Your registration for <strong>{society.name}</strong> is pending approval by the society admin.
          </p>
          <div className="p-4 mb-6 border text-left" style={{ backgroundColor: '#F4F4F5', borderColor: '#E4E4E7' }}>
            <p className="text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
              <strong>Flat:</strong> {submitResult.flat_number}
            </p>
            <p className="text-sm mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
              <strong>Parking Spots:</strong> {submitResult.parking_spots.join(', ')}
            </p>
          </div>
          <p className="text-xs" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
            You will be able to login once approved.
          </p>
          <Button onClick={() => navigate('/auth/owner')} className="mt-4" variant="outline" data-testid="go-to-login-btn" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ backgroundColor: '#F4F4F5' }}>
      <div className="w-full max-w-lg" data-testid="society-registration-form">
        {/* Header */}
        <div className="p-6 border-b text-center" style={{ backgroundColor: '#09090B' }}>
          <div className="flex items-center justify-center gap-3 mb-2">
            <Building2 size={28} style={{ color: '#FBBF24' }} />
            <h1 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Cabinet Grotesk, sans-serif', color: '#FFFFFF' }} data-testid="society-name-display">
              {society.name}
            </h1>
          </div>
          <p className="text-xs tracking-[0.2em] uppercase" style={{ color: '#A1A1AA', fontFamily: 'IBM Plex Sans, sans-serif' }}>
            Owner Registration
          </p>
        </div>

        <div className="p-6 border" style={{ backgroundColor: '#FFFFFF', borderColor: '#E4E4E7' }}>
          {/* Step 1: Select Building */}
          <div className="mb-5">
            <Label className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
              <MapPin size={14} className="inline mr-1" />
              Select Building / Tower
            </Label>
            <select
              value={selectedBuilding}
              onChange={(e) => setSelectedBuilding(e.target.value)}
              data-testid="building-select"
              className="w-full mt-2 p-3 border text-sm"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif', borderColor: '#E4E4E7', backgroundColor: '#FFFFFF' }}
            >
              <option value="">-- Select Building --</option>
              {buildings.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          </div>

          {/* Step 2: Select Flat */}
          {selectedBuilding && (
            <div className="mb-5">
              <Label className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                Select Flat Number
              </Label>
              {flats.length === 0 ? (
                <p className="text-sm mt-2" style={{ color: '#71717A' }}>No flats found in this building.</p>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
                  {flats.map((flat) => (
                    <button
                      key={flat.id}
                      onClick={() => !flat.owner_registered && setSelectedFlat(flat)}
                      disabled={flat.owner_registered}
                      data-testid={`flat-option-${flat.flat_number}`}
                      className="p-3 border text-left transition-colors"
                      style={{
                        borderColor: flat.owner_registered ? '#E4E4E7' : selectedFlat?.id === flat.id ? '#FBBF24' : '#E4E4E7',
                        backgroundColor: flat.owner_registered ? '#F4F4F5' : selectedFlat?.id === flat.id ? '#FFFBEB' : '#FFFFFF',
                        borderWidth: selectedFlat?.id === flat.id ? '2px' : '1px',
                        opacity: flat.owner_registered ? 0.5 : 1,
                        cursor: flat.owner_registered ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <p className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{flat.flat_number}</p>
                      <p className="text-xs mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: flat.owner_registered ? '#DC2626' : '#71717A' }}>
                        {flat.owner_registered ? 'Already registered' : `${flat.parking_spots.length} spot${flat.parking_spots.length !== 1 ? 's' : ''}`}
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Show Selected Flat's Parking Spots */}
          {selectedFlat && (
            <div className="mb-5 p-4" style={{ backgroundColor: '#F4F4F5' }} data-testid="selected-flat-info">
              <p className="text-xs tracking-[0.2em] uppercase font-bold mb-2" style={{ color: '#71717A' }}>Your Parking Spots</p>
              <div className="flex gap-2 flex-wrap">
                {selectedFlat.parking_spots.map((spot) => (
                  <div key={spot.id || spot.spot_number} className="flex items-center gap-2 px-3 py-2" style={{ backgroundColor: '#FBBF24' }}>
                    <Car size={14} style={{ color: '#09090B' }} />
                    <span className="text-sm font-bold" style={{ fontFamily: 'JetBrains Mono, monospace', color: '#09090B' }}>{spot.spot_number}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Personal Details */}
          {selectedFlat && (
            <div className="space-y-4 mb-6">
              <div>
                <Label className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                  <User size={14} className="inline mr-1" />
                  Your Name
                </Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Rahul Sharma"
                  data-testid="reg-name-input"
                  className="mt-2"
                />
              </div>
              <div>
                <Label className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                  <Phone size={14} className="inline mr-1" />
                  Phone Number
                </Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value.replace(/\D/g, '').slice(0, 10) })}
                  placeholder="10-digit mobile number"
                  data-testid="reg-phone-input"
                  className="mt-2"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                />
              </div>
              <div>
                <Label className="text-sm font-bold" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#09090B' }}>
                  Password
                </Label>
                <Input
                  type="password"
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="Create a password"
                  data-testid="reg-password-input"
                  className="mt-2"
                />
              </div>
            </div>
          )}

          {/* Submit */}
          {selectedFlat && (
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              data-testid="register-submit-btn"
              className="w-full p-4 transition-transform hover:-translate-y-1"
              style={{ backgroundColor: '#09090B', color: '#FFFFFF', fontFamily: 'Cabinet Grotesk, sans-serif', fontWeight: 'bold' }}
            >
              {submitting ? 'Submitting...' : 'Register'}
            </Button>
          )}

          <p className="text-xs text-center mt-4" style={{ fontFamily: 'IBM Plex Sans, sans-serif', color: '#71717A' }}>
            Already registered? <a href="/auth/owner" className="underline" style={{ color: '#09090B' }}>Login here</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default SocietyRegistration;
